import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import { Icon, Label, Tooltip, Typography } from '../ui-components';

interface PresenceEventData {
  browser?: string;
  browserver? : string;
  userAgent?: string;
  os?: string;
  operatingSystem?: string;
  platform? : string;
  url?: string;
  currentUrl?: string;
  sessionCapabilities?: any;
  application?: any;
}


interface VisitorInfoCardProps {
  presenceData: PresenceEventData | null;
  showMCS?: boolean;
}


type SessionType = 'Unknown' | 'Desktop Web' | 'Mobile Web' | 'Mobile App';

const VisitorInfoCard: React.FC<VisitorInfoCardProps> = ({ 
  presenceData
}) => {
  const detailsId = useId();
  const headingId = useId();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const headerButtonRef = useRef<HTMLButtonElement | null>(null);
  const detailsRef = useRef<HTMLDivElement | null>(null);
  const detailsContentRef = useRef<HTMLDivElement | null>(null);
  const [detailsMaxHeight, setDetailsMaxHeight] = useState<number>(0);

  const DEFAULT_OS = 'Unknown';
  const DEFAULT_URL = 'Unknown';
  const DEFAULT_BROWSER = 'Unknown';

  const getBrowserInfo = (): string => {
    if (!presenceData) return DEFAULT_BROWSER;
    const browser = [presenceData.browser, presenceData.browserver].filter(Boolean).join(' ').trim();
    return browser || presenceData.userAgent || DEFAULT_BROWSER;
  };

  const getOSInfo = (): string => {
    if (!presenceData) return DEFAULT_OS;
    return presenceData.platform || presenceData.operatingSystem || DEFAULT_OS;
  };

  const getURL = (): string => {
    if (!presenceData) return DEFAULT_URL;
    return presenceData.url || presenceData.currentUrl || DEFAULT_URL;
  };

  const browserInfo = getBrowserInfo();
  const osInfo = getOSInfo();
  const url = getURL();

  const getSessionType = (): SessionType => {
    if (!presenceData) return 'Unknown';

    const platform = (presenceData.platform || '').toLowerCase();
    const os = (osInfo || '').toLowerCase();
    const ua = (presenceData.userAgent || '').toLowerCase();

    const hasBrowser = !!browserInfo && browserInfo.trim() !== '';
    
    const hasNativeAppSignals = !!presenceData.sessionCapabilities || !!presenceData.application;
    
    const isIOS =
      platform.includes('ios') ||
      platform.includes('iphone') ||
      platform.includes('ipad') ||
      os.includes('ios') ||
      os.includes('iphone') ||
      os.includes('ipad') ||
      /iphone|ipad|ipod/.test(ua);
    
    const isAndroid =
      platform.includes('android') || os.includes('android') || /android/.test(ua);

    const isWindows =
      platform.includes('win') || platform.includes('windows') || os.includes('windows');
    const isMac =
      platform.includes('mac') || os.includes('mac') || os.includes('macos');
    const isLinux = platform.includes('linux') || os.includes('linux');

    const isMobile = isIOS || isAndroid;
    const isDesktop = isWindows || isMac || isLinux;

    // Web sessions are determined by browser presence.
    if (hasBrowser) {
      return isMobile ? 'Mobile Web' : 'Desktop Web';
    }

    // No browser: treat as native app. We only support Mobile App as a distinct type.
    if (hasNativeAppSignals) {
      return isDesktop ? 'Unknown' : 'Mobile App';
    }

    if (isMobile) return 'Mobile App';
    return 'Unknown';
  };

  const getPillClasses = (sessionType: SessionType): string => {
    const themes: Record<SessionType, string> = {
      // Text colors are intentionally one step lighter than before, while still meeting WCAG 2.1
      // contrast ratio >= 4.5:1 against the chosen background colors.
      'Mobile App': 'bg-emerald-50 text-emerald-700 ring-emerald-200',
      'Mobile Web': 'bg-amber-50 text-amber-700 ring-amber-200',
      'Desktop Web': 'bg-sky-50 text-sky-700 ring-sky-200',
      'Unknown': 'bg-slate-100 text-slate-600 ring-slate-200',
    };
    return themes[sessionType] || themes.Unknown;
  };


  const sessionType = getSessionType();
  const pillClasses = getPillClasses(sessionType);
  const typeIconName =
    sessionType === 'Desktop Web'
      ? 'monitor'
      : sessionType === 'Mobile Web' || sessionType === 'Mobile App'
        ? 'smartphone'
        : 'info';
  const badgeDisplayTypes: SessionType[] = [
    'Unknown',
    'Desktop Web',
    'Mobile Web',
    'Mobile App',
  ];

  const renderTypePill = (t: SessionType) => {
    const classes = getPillClasses(t);
    const isMobileType = t === 'Mobile Web' || t === 'Mobile App';
    const isDesktopWebType = t === 'Desktop Web';
    const isUnknownType = t === 'Unknown';
    const icon =
      t === 'Desktop Web'
        ? 'show-screen'
        : t === 'Mobile Web' || t === 'Mobile App'
          ? 'mobile'
          : 'help';

    return (
      <div className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ring-1 leading-none ${classes}`}>
        <Icon
          name={icon}
          weight={isDesktopWebType || isMobileType || isUnknownType ? 'bold' : undefined}
          // Use the smallest DS size and constrain line-height via CSS (see GlanceStates.css)
          size="3"
          className="gds-badge-icon shrink-0 text-current"
          aria-hidden="true"
          title=""
        />
        <Typography tag="span" variant="body10" bodyWeight="semibold" className="whitespace-nowrap">
          {t}
        </Typography>
      </div>
    );
  };

  useLayoutEffect(() => {
    const el = detailsContentRef.current;
    if (!el) return;

    const update = () => {
      // Measure actual content height so the container expands fully (e.g. when URL wraps).
      setDetailsMaxHeight(el.scrollHeight);
    };

    update();

    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    window.addEventListener('resize', update);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  return (
    <div className="w-full max-w-full px-2 mt-2">
      <div className="w-full max-w-[400px] min-w-[min(400px,100%)] rounded-2xl bg-[theme('colors.neutral-border.subtle')] p-px">
        <section
          className={[
            'w-full rounded-2xl bg-[#FBFCFF] px-3',
            // When collapsed, keep equal padding above/below the header row.
            isCollapsed ? 'py-0' : 'pt-0 pb-[10px]',
          ].join(' ')}
        >
        <button
          type="button"
          ref={headerButtonRef}
          className={[
            'flex w-full items-center justify-between gap-2 text-left',
            // When collapsed, let the section's symmetric padding provide spacing.
            isCollapsed ? 'py-0' : 'pt-0 pb-1',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-border-cloudy focus-visible:ring-offset-2 focus-visible:ring-offset-[#FBFCFF] rounded-md',
          ].join(' ')}
          aria-expanded={!isCollapsed}
          aria-controls={detailsId}
          aria-labelledby={headingId}
          onClick={() =>
            setIsCollapsed((v) => {
              const next = !v;
              if (next) {
                // If focus is inside the collapsing region, restore focus to the trigger.
                requestAnimationFrame(() => {
                  if (detailsRef.current?.contains(document.activeElement)) {
                    headerButtonRef.current?.focus();
                  }
                });
              }
              return next;
            })
          }
        >
          <Typography
            id={headingId}
            tag="div"
            variant="body10"
            bodyWeight="semibold"
            className="min-w-0 truncate"
          >
            Visitor System Info
          </Typography>
          <span className="flex items-center gap-1">
            <Icon name="info" size="sm" aria-hidden="true" title="" />
            <Icon
              // Pattern: collapsed = down, expanded = up (we rotate the down chevron).
              name="chevron-down"
              // DS Icon sizes are mapped to Tailwind spacing keys; use CSS var for exact px sizing.
              className={[
                'mt-2 transition-transform duration-200 ease-out motion-reduce:transition-none',
                'text-neutral-on-surface-text-bright',
                isCollapsed ? 'rotate-0' : 'rotate-180',
              ].join(' ')}
              style={{ ['--icon-size' as any]: '16px' }}
              aria-hidden="true"
              title=""
            />
          </span>
        </button>

        <div
          id={detailsId}
          ref={detailsRef}
          className={[
            'overflow-hidden motion-reduce:transition-none transition-[max-height,opacity,transform] duration-200 ease-out',
            isCollapsed ? 'opacity-0 -translate-y-1 pointer-events-none' : 'opacity-100 translate-y-0',
          ].join(' ')}
          style={{ maxHeight: isCollapsed ? 0 : detailsMaxHeight }}
          aria-hidden={isCollapsed}
          role="region"
          aria-labelledby={headingId}
          // @ts-expect-error: inert is a valid HTML attribute; used to prevent focus when collapsed.
          inert={isCollapsed ? '' : undefined}
        >
          <div ref={detailsContentRef}>
            <dl className="mt-0 grid grid-cols-[max-content,minmax(0,1fr)] gap-x-2 gap-y-1">
              <Label tag="dt" text="Type" size="x-small" weight="medium" className="flex min-h-5 items-center leading-none" />
              <dd className="flex min-h-5 min-w-0 items-center leading-none">
                {renderTypePill(sessionType)}
              </dd>

              <Label tag="dt" text="Browser" size="x-small" weight="medium" className="mt-1 flex min-h-5 items-start leading-none pt-0" />
              <Typography
                tag="dd"
                variant="body10"
                className="mt-1 flex min-h-5 min-w-0 items-start text-slate-900 leading-none pt-0"
              >
                <span className="truncate">{browserInfo}</span>
              </Typography>

              <Label tag="dt" text="OS" size="x-small" weight="medium" className="flex min-h-5 items-start leading-none pt-0" />
              <Typography
                tag="dd"
                variant="body10"
                className="flex min-h-5 min-w-0 items-start text-slate-900 leading-none pt-0"
              >
                <span className="truncate">{osInfo}</span>
              </Typography>

              <Label tag="dt" text="URL" size="x-small" weight="medium" className="flex min-h-5 items-start leading-none pt-0 pb-1" />
              <dd className="flex min-w-0 items-start leading-none pt-0 pb-1">
                {url ? (
                  <Tooltip text={url} position="top" size="small" inline>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block max-w-full whitespace-normal break-words [overflow-wrap:anywhere] underline decoration-slate-300 hover:decoration-slate-500 text-blue-800"
                      title={url}
                      aria-label={`Open URL in a new tab: ${url}`}
                    >
                      <Typography tag="span" variant="body10">
                        {url}
                      </Typography>
                    </a>
                  </Tooltip>
                ) : (
                  <Typography tag="span" variant="body10" className="text-slate-500">
                    —
                  </Typography>
                )}
              </dd>
            </dl>
          </div>
        </div>
        </section>
      </div>
    </div>
  );
};

export default VisitorInfoCard;